/**
 * @file 漫画详情面包屑状态
 * @description 使用浏览器 history state 保存进入漫画详情前的来源，不污染业务 URL。
 */
import { readonly, ref } from 'vue'

/** history state 中保存漫画来源路径的字段名。 */
export const MANGA_SOURCE_STATE_KEY = 'tdmMangaSourcePath'

/** 可作为漫画详情来源的路由信息。 */
export interface MangaRouteSourceCandidate {
  /** 不含查询参数的来源路由路径。 */
  path: string
  /** 包含查询参数与 hash 的完整来源路径。 */
  fullPath: string
  /** 来源路由实际匹配到的路由记录数量。 */
  matchedCount: number
}

/** 漫画详情路由路径规则，排除漫画管理等静态页面。 */
const MANGA_DETAIL_PATH_PATTERN = /^\/manga\/\d+\/?$/

/** 不应出现在漫画来源面包屑中的过渡页面。 */
const IGNORED_SOURCE_PATHS = new Set(['/', '/login'])

/**
 * 判断字符串是否为站内绝对路由路径。
 * @param path 待判断的完整路径。
 * @returns 单斜杠开头且不是协议相对地址时返回 true。
 */
const isInternalRoutePath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//')

/** 动态来源页面与其展示名称。 */
const MANGA_SOURCE_LABEL_RULES: ReadonlyArray<{
  /** 匹配来源路由的规则。 */
  pattern: RegExp
  /** 面包屑展示名称。 */
  label: string
}> = [
  { pattern: /^\/workbench\/?$/, label: '工作台' },
  { pattern: /^\/search\/?$/, label: '搜索结果' },
  { pattern: /^\/author\/[^/]+\/?$/, label: '作者作品' },
  { pattern: /^\/magazine\/[^/]+\/?$/, label: '杂志作品' },
  { pattern: /^\/member\/list\/?$/, label: '组员名录' },
  { pattern: /^\/member\/[^/]+\/?$/, label: '组员详情' },
  { pattern: /^\/editor\/episode\/[^/]+\/?$/, label: '在线编辑器' }
]

/** 当前漫画详情的真实译名。 */
const mangaBreadcrumbTitleRef = ref('')

/** 供顶栏只读订阅的当前漫画译名。 */
export const mangaBreadcrumbTitle = readonly(mangaBreadcrumbTitleRef)

/**
 * 判断路径是否为漫画详情页。
 * @param path 待判断的路由路径。
 * @returns 路径匹配 `/manga/:mangaId` 时返回 true。
 */
export const isMangaDetailPath = (path: string): boolean =>
  MANGA_DETAIL_PATH_PATTERN.test(path)

/**
 * 判断路由切换时是否应清空上一部漫画的面包屑译名。
 * @param destinationRouteName 目标路由名称。
 * @param destinationMangaId 目标漫画 ID 路由参数。
 * @param sourceRouteName 来源路由名称。
 * @param sourceMangaId 来源漫画 ID 路由参数。
 * @returns 进入 Manga 或切换漫画 ID 时返回 true；同一漫画仅变更查询参数时返回 false。
 */
export const shouldClearMangaBreadcrumbTitle = (
  destinationRouteName: unknown,
  destinationMangaId: unknown,
  sourceRouteName: unknown,
  sourceMangaId: unknown
): boolean =>
  destinationRouteName === 'manga' &&
  (sourceRouteName !== 'manga' || destinationMangaId !== sourceMangaId)

/**
 * 解析可写入漫画详情 history state 的来源路径。
 * @param destinationPath 目标路由路径。
 * @param source 来源路由信息。
 * @returns 有效来源的完整路径；直接访问、首页、登录页或漫画详情间跳转返回 null。
 */
export const resolveMangaSourcePath = (
  destinationPath: string,
  source: MangaRouteSourceCandidate
): string | null => {
  if (!isMangaDetailPath(destinationPath)) return null
  if (source.matchedCount === 0) return null

  const normalizedSourcePath = source.path.replace(/\/+$/, '') || '/'
  if (
    IGNORED_SOURCE_PATHS.has(normalizedSourcePath) ||
    isMangaDetailPath(normalizedSourcePath) ||
    !isInternalRoutePath(source.fullPath)
  ) {
    return null
  }
  return source.fullPath
}

/**
 * 从 history state 读取漫画来源路径。
 * @param state 待读取的 history state；省略时读取当前浏览器记录。
 * @returns 合法的站内完整路径；字段缺失或格式异常时返回 null。
 */
export const readMangaSourcePath = (state?: unknown): string | null => {
  const historyState =
    state ?? (typeof window === 'undefined' ? undefined : window.history.state)
  if (!historyState || typeof historyState !== 'object') return null

  const sourcePath = (historyState as Record<string, unknown>)[
    MANGA_SOURCE_STATE_KEY
  ]
  return typeof sourcePath === 'string' && isInternalRoutePath(sourcePath)
    ? sourcePath
    : null
}

/**
 * 将漫画来源路径合并进当前 history state。
 * @param sourcePath 进入漫画详情前的完整来源路径。
 * @returns 非浏览器环境或路径无效时不执行写入。
 */
export const writeMangaSourcePath = (sourcePath: string): void => {
  if (typeof window === 'undefined' || !isInternalRoutePath(sourcePath)) return
  const currentState =
    window.history.state && typeof window.history.state === 'object'
      ? window.history.state
      : {}
  window.history.replaceState(
    { ...currentState, [MANGA_SOURCE_STATE_KEY]: sourcePath },
    ''
  )
}

/**
 * 生成漫画来源面包屑名称。
 * @param path 来源路由路径。
 * @param fallbackLabel 静态导航提供的回退名称。
 * @returns 动态页面的业务名称，未命中时返回静态名称或“来源页面”。
 */
export const getMangaSourceLabel = (
  path: string,
  fallbackLabel?: string
): string => {
  const normalizedPath = path.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/'
  const matchedRule = MANGA_SOURCE_LABEL_RULES.find((rule) =>
    rule.pattern.test(normalizedPath)
  )
  return matchedRule?.label || fallbackLabel || '来源页面'
}

/**
 * 更新当前漫画面包屑译名。
 * @param title 漫画详情接口返回的译名。
 * @returns 空白名称会被归一为空字符串。
 */
export const setMangaBreadcrumbTitle = (title?: string | null): void => {
  mangaBreadcrumbTitleRef.value = title?.trim() || ''
}

/** 清空上一部漫画残留的面包屑译名。 */
export const clearMangaBreadcrumbTitle = (): void => {
  mangaBreadcrumbTitleRef.value = ''
}
